import { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import {
  SERIES,
  STEPS,
  SeriesDef,
  SiteWalk,
  liveSeries,
} from './stockWaterfall.walk'
import { rgba } from './stockWaterfall.figure'
import { FAMILY_COLOURS, SAFETY_STOCK_COLOUR } from './StockWaterfall.constants'

const swatch = (series: SeriesDef) => {
  const hue = FAMILY_COLOURS[series.family]
  const base = {
    width: '14px',
    height: '10px',
    borderRadius: '2px',
    border: `1.5px solid ${hue}`,
    mr: 0.75,
    flex: 'none',
  }
  if (series.fill === 'hatched') {
    return {
      ...base,
      background: `repeating-linear-gradient(135deg, ${hue} 0 1.6px, transparent 1.6px 4.5px)`,
    }
  }
  if (series.fill === 'crossHatched') {
    return {
      ...base,
      background:
        `repeating-linear-gradient(45deg, ${hue} 0 1.2px, transparent 1.2px 4px),` +
        `repeating-linear-gradient(135deg, ${hue} 0 1.2px, transparent 1.2px 4px)`,
    }
  }
  if (series.fill === 'light') {
    return { ...base, background: rgba(hue, 0.35) }
  }
  return { ...base, background: hue }
}

type StockWaterfallLegendProps = {
  sites: SiteWalk[]
}

// A legend entry per series, grouped by step; series with no value in the
// shown window are dimmed.
export const StockWaterfallLegend = ({ sites }: StockWaterfallLegendProps) => {
  const live = useMemo(() => liveSeries(sites), [sites])
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 2.5,
        rowGap: 0.5,
      }}
    >
      {STEPS.map((step) => (
        <Box
          key={step.id}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {step.label}
          </Typography>
          {SERIES.filter((s) => s.step === step.id).map((series) => (
            <Box
              key={series.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                opacity: live.has(series.id) ? 1 : 0.4,
              }}
            >
              <Box sx={swatch(series)} />
              <Typography variant="caption">{series.label}</Typography>
            </Box>
          ))}
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '16px',
            borderTop: `2px dashed ${SAFETY_STOCK_COLOUR}`,
            mr: 0.75,
          }}
        />
        <Typography variant="caption">Safety stock</Typography>
      </Box>
    </Box>
  )
}
